import React, { useEffect, useState, useMemo } from "react";
import { Input, Space, Button, notification, Modal, Form } from "antd";
import { ReactSVG } from "react-svg";
import { filter, includes, forEach, isEmpty } from "lodash";
import { Link } from "react-router-dom";
import moment from "moment";
import Cookies from "js-cookie";
import socketIOClient from "socket.io-client";
import { PlusOutlined } from "@ant-design/icons";
import "./RoutePlanDetail.scss";
import ItemContainer from "../../modules/routePlan/containers/Item";
import withPage from "./withPage";
import RoutePlanLeft from "../RoutePlan/RoutePlanLeft";
import DriversTable from "../Table/Drivers";
import StopsMapbox from "../Mapbox/StopsMapbox";

import { DATE_FORMAT_FRONTEND } from "../../constants";
import { getTranslation, getStopsOrder, splitFirstOccurrence } from "../../utils/helper";
import DriverForm from "../Forms/Drivers/Driver";

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const PAGE_SIZE = 100;

const defaultCountryCode = window.location.href.includes("nordicrouting.se") ? "se" : "sg";

const RoutePlanAddDriversPage = ({
  isFetching,
  id,
  routePlan,
  drivers,
  metadataDrivers,
  query,
  pushTo,
  doGetAllDrivers,
  doAddDriversRoutePlan,
  doOptimizeRoutePlan,
  doDetailRoutePlan,
  handleFailedOptimize,
  doCreateDriver,
  history,
  user,
}) => {
  const setting = user.company;

  const [form] = Form.useForm();

  const driverIds = routePlan?.drivers?.map(d => d.driver._id);
  const topDriverIds = JSON.parse(Cookies.get("driverIdsOfNewestOrder") || "[]");
  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadataDrivers.totalItems - driverIds?.length,
    showTotal: total => `${total} ${getTranslation(setting, "Drivers", "Drivers")}`,
  };

  const sortedDrivers = useMemo(() => {
    if (!drivers?.length || !topDriverIds) return drivers;
    return drivers.sort((driver1, driver2) => topDriverIds.indexOf(driver2._id) - topDriverIds.indexOf(driver1._id));
  }, [drivers]);

  const orders = routePlan?.orders || [];
  const routes = routePlan?.drivers || [];

  const [selectedDrivers, setSelectedDrivers] = useState(null);
  const [state, setState] = useState({});
  const [stops, setStops] = useState([]);
  const [countryCodeInit, setCountryCodeInit] = useState(defaultCountryCode);

  const [stateCreateDriver, setStateCreateDriver] = useState({
    visibleModal: false,
    action: "",
  });

  const [api, contextHolder] = notification.useNotification();

  const canEdit = !user?.roles?.includes("user");

  const defaultSelectedItems = !topDriverIds?.length
    ? []
    : sortedDrivers?.filter(driver => topDriverIds.indexOf(driver._id) !== -1);

  useEffect(() => {
    setCountryCodeInit(Cookies.get(`CountryCode-${user._id}`) || defaultCountryCode);
  }, []);

  useEffect(() => {
    if (orders?.length > 0) {
      let stopsT = [];
      forEach(orders, order => {
        stopsT = stopsT.concat(getStopsOrder(order));
      });
      setStops(stopsT);
    }
  }, [orders]);

  useEffect(() => {
    if (query.stopId) {
      const stop = stops.find(s => s.id === query.stopId);
      setTimeout(() => {
        onClickStop(stop);
      }, 1000);
    }
  }, [stops]);

  const onClosePopup = () => {
    setState({ ...state, stopClicked: { id: "closed" }, routeClicked: {} });
  };

  const onClickStop = stop => {
    setState({ ...state, stopClicked: stop, routeClicked: {} });
  };

  const onClickRoute = route => {
    setState({ ...state, stopClicked: {}, routeClicked: route });
  };

  const onClickCreateDriver = () => {
    form.resetFields();

    form.setFieldsValue({
      speed: 50, // 50: Normal
    });
    setStateCreateDriver({ ...stateCreateDriver, visibleModal: true });
  };

  const handleCancelCreateDriver = () => {
    setStateCreateDriver({ ...stateCreateDriver, visibleModal: false });
    form.resetFields();
  };

  const setParamsCreateDriver = values => {
    const params = { ...values };
    params.startAddress = values.startLocation?.address;
    params.startLat = values.startLocation?.latLng?.lat;
    params.startLng = values.startLocation?.latLng?.lng;
    params.endAddress = values.endLocation?.address;
    params.endLat = values.endLocation?.latLng?.lat;
    params.endLng = values.endLocation?.latLng?.lng;
    params.shiftStart = values.shiftStart;
    params.shiftEnd = values.shiftEnd;
    params.email = values.email || null;
    params.skills = values.skills || [];
    return params;
  };

  const handleSubmitCreateDriver = async values => {
    const params = setParamsCreateDriver(values);
    const data = await doCreateDriver(params);
    await doGetAllDrivers({ itemsPerPage: 1000 });
    if (data?.payload?._id) {
      setSelectedDrivers([...(selectedDrivers || []), ...defaultSelectedItems, data?.payload]);
    }
    handleCancelCreateDriver();
  };

  const handleAddDriversAndOptimizeRoute = async selectedDrivers => {
    const selectedDriverIds = selectedDrivers.map(s => s._id);

    await doAddDriversRoutePlan({ id, drivers: selectedDriverIds });
    await doOptimizeRoutePlan({
      id,
      forceAll: false,
      applyBalanceSetting: true,
    });
    Cookies.set("driverIdsOfNewestOrder", selectedDriverIds);
  };

  const handleBulkUpdate = selectedDrivers => {
    return {
      title: `${selectedDrivers.length} ${getTranslation(setting, "Route Plans", "drivers selected")}`,
      actions: (
        <Space>
          <Button
            className="btn-add-to-route"
            size="large"
            key="AddToRoute"
            onClick={() => handleAddDriversAndOptimizeRoute(selectedDrivers)}
            type="primary"
            disabled={selectedDrivers.length <= 0}>
            {getTranslation(setting, "Route Plans", "Add to Route and Optimize")}
            <ReactSVG className="icon" src="/icons/add-to-route.svg" />
          </Button>
        </Space>
      ),
    };
  };

  const handleOnSearch = e => {
    console.log(e.target.value);
  };

  const handleFilter = (params, updatePath) => {
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params?.currentPage) || 1;
    const itemsPerPage = Number(params?.itemsPerPage) || PAGE_SIZE;
    doGetAllDrivers({
      currentPage,
      itemsPerPage,
      ...params,
    });
  };

  const handleTableChange = params => {
    handleFilter(params, true);
  };

  useEffect(() => {
    doGetAllDrivers({ itemsPerPage: 1000 });
  }, [routePlan]);

  useEffect(() => {
    const handler = async data => {
      if (data.event === "routePlan.optimizing.completed" && data.payload.routePlanId === id) {
        console.log("routePlan.optimizing.completed");
        console.log(data);
        await doDetailRoutePlan(id);
        history.push(`/route-plans/${id}`);
      }
      if (data.event === "routePlan.optimizing.error" && data.payload.routePlanId === id) {
        console.log("routePlan.optimizing.error");
        const drivers = routePlan?.drivers?.map(r => r.driver) || [];
        handleFailedOptimize();
        let message = data.payload.message || "";
        if (message.includes("Visit")) {
          const split1 = splitFirstOccurrence(message, ":");
          const split2 = splitFirstOccurrence(split1[0], " ");
          if (split1.length === 2 && split2.length === 2 && split2[1].includes("_")) {
            const split3 = split2[1].split("_");
            const orderId = split3[0];
            const order = orders.find(o => o._id == orderId);
            if (order) message = order.orderCode + ": " + split1[1];
          }
        } else if (message.includes("_pickup") || message.includes("_dropoff")) {
          const message_arr = message?.split(" ") || [];
          message = message_arr
            .map(m => {
              if (m.includes("_pickup")) {
                const order_type = m.split("_");
                const orderId = order_type[0];
                const order = orders.find(o => o._id == orderId);
                if (order) {
                  return `Pickup ${order.orderCode}`;
                } else {
                  return m;
                }
              } else if (m.includes("_dropoff")) {
                const order_type = m.split("_");
                const orderId = order_type[0];
                const order = orders.find(o => o._id == orderId);
                if (order) {
                  return `Delivery ${order.orderCode}`;
                } else {
                  return m;
                }
              } else {
                return m;
              }
            })
            .join(" ");
        } else {
          const split1 = splitFirstOccurrence(message, ":");
          const driver = drivers.find(d => d._id === split1[0]);
          if (driver) message = driver.name + ": " + split1[1];
        }
        api.error({
          message,
          placement: "bottomRight",
        });
      }
    };
    socket.connect();
    socket.on(user.company?._id, handler);
    return () => socket.off(user.company?._id, handler);
  }, [routePlan]);

  const deliveryDate = moment(routePlan?.deliveryDate).utc().format(DATE_FORMAT_FRONTEND);

  return (
    <div className="Content ContentFull RoutePlanDetailPage RoutePlanAddDriversPage">
      {contextHolder}
      <RoutePlanLeft routePlan={routePlan} />
      <div className="ContentMain">
        <div className="MainLeft">
          <div className="MainLeft__Header">
            <div className="MainLeft__Header__Title">
              <Link to="/route-plans">{getTranslation(setting, "Route Plans", "Route Plans")}</Link>
              <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
              <Link to={`/route-plans/${routePlan?._id}/drivers`}>
                <span>
                  {routePlan?.name} (<span className="Capitalize">{deliveryDate}</span>)
                </span>
              </Link>
              <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
              <span className="Link">{getTranslation(setting, "Route Plans", "Add Drivers")}</span>
            </div>
            <div className="MainLeft__Header__Search">
              <Input.Search
                size="large"
                placeholder={getTranslation(setting, "Route Plans", "Search Anything")}
                onChange={handleOnSearch}
              />
              {sortedDrivers && sortedDrivers.length > 0 && (
                <Button type="primary" size="large" onClick={onClickCreateDriver} disabled={!isEmpty(selectedDrivers)}>
                  {getTranslation(setting, "Route Plans", "Create New Driver")}
                  <PlusOutlined />
                </Button>
              )}
            </div>
          </div>
          <DriversTable
            setting={setting}
            rowKey="_id"
            loading={isFetching}
            pagination={pagination}
            dataSource={filter(sortedDrivers, d => !includes(driverIds, d._id))}
            defaultSelectedItems={defaultSelectedItems}
            scrollHeight="calc(100vh - 610px)"
            bulkUpdate={handleBulkUpdate}
            onChange2={handleTableChange}
            query={query}
            selectedItems={selectedDrivers}
            setSelectedItems={setSelectedDrivers}
          />
          {(sortedDrivers || []).length === 0 && (
            <div className="MainLeft__Footer">
              <span>You don't have any drivers in your fleet. Please create new driver and add to route</span>
              <div className="action">
                <Button type="primary" size="large" onClick={onClickCreateDriver} disabled={!isEmpty(selectedDrivers)}>
                  {getTranslation(setting, "Route Plans", "Create New Driver")}
                  <PlusOutlined />
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="MainRight">
          <StopsMapbox
            routes={canEdit ? routes : []}
            stops={filter(stops, s => s.lat && s.lng)}
            orders={orders}
            stopClicked={state.stopClicked}
            onClickStop={onClickStop}
            routeClicked={state.routeClicked}
            onClickRoute={onClickRoute}
            onClosePopup={onClosePopup}
            selectedDrivers={selectedDrivers ? selectedDrivers : defaultSelectedItems}
          />
        </div>
      </div>
      <Modal
        open={stateCreateDriver.visibleModal}
        onCancel={handleCancelCreateDriver}
        forceRender
        footer={[]}
        width={792}>
        <DriverForm
          form={form}
          isFetching={isFetching}
          driver={null}
          countryCodeInit={countryCodeInit}
          handleSubmit={handleSubmitCreateDriver}
          handleCancel={handleCancelCreateDriver}
        />
      </Modal>
    </div>
  );
};

export default withPage(ItemContainer(RoutePlanAddDriversPage));
